import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getAnalytics, logEvent } from 'firebase/analytics';
import './Guides.css';
import ContributorMapsSection from './ContributorMapsSection';
import ContributorAttributionBanner from './ContributorAttributionBanner';
import RestaurantPopup from './RestaurantPopup'; // Make sure to create this file!

// ... (Keep your cities object as is) ...
const cities = {
  'santa-barbara': {
    name: 'Santa Barbara',
    description: 'Coastal California\'s curated dining destinations',
    center: { latitude: 34.4208, longitude: -119.6982 },
    span: { latitudeDelta: 0.3, longitudeDelta: 0.3 },
    collection: 'restaurants - santa barbara'
  },
  'san-francisco': {
    name: 'San Francisco Bay Area',
    description: 'Bay Area\'s premier dining destinations',
    center: { latitude: 37.7749, longitude: -122.4194 },
    span: { latitudeDelta: 0.3, longitudeDelta: 0.3 },
    collection: 'restaurants - bay area'
  },
  'carmel-monterey': {
    name: 'Carmel & Monterey',
    description: 'Coastal California\'s culinary gems',
    center: { latitude: 36.5527, longitude: -121.9233 },
    span: { latitudeDelta: 0.4, longitudeDelta: 0.4 },
    collection: 'restaurants - carmel+monterey'
  },
  'new-york': {
    name: 'New York City',
    description: 'Manhattan & Brooklyn\'s finest dining destinations',
    center: { latitude: 40.7128, longitude: -74.0060 },
    span: { latitudeDelta: 0.5, longitudeDelta: 0.5 },
    collection: 'restaurants - new york'
  },
  'napa': {
    name: 'Napa Valley',
    description: 'Wine country\'s culinary excellence',
    center: { latitude: 38.2975, longitude: -122.3011 },
    span: { latitudeDelta: 0.4, longitudeDelta: 0.4 },
    collection: 'restaurants - napa'
  },
  'mexico-city': {
    name: 'Mexico City',
    description: 'Mexico\'s vibrant culinary capital',
    center: { latitude: 19.4326, longitude: -99.1332 },
    span: { latitudeDelta: 0.3, longitudeDelta: 0.3 },
    collection: 'restaurants - mexico city'
  },
  'hawaii-island': {
    name: 'Hawaii Island',
    description: 'Big Island\'s tropical dining experiences',
    center: { latitude: 19.8968, longitude: -155.5828 },
    span: { latitudeDelta: 0.8, longitudeDelta: 0.8 },
    collection: 'restaurants - hawaii island'
  },
  'breckenridge': {
    name: 'Breckenridge',
    description: 'Mountain dining at altitude',
    center: { latitude: 39.4817, longitude: -106.0384 },
    span: { latitudeDelta: 0.2, longitudeDelta: 0.2 },
    collection: 'restaurants - breckenridge'
  },
  'laguna-newport': {
    name: 'Laguna Beach & Newport',
    description: 'Orange County\'s coastal cuisine',
    center: { latitude: 33.5427, longitude: -117.7854 },
    span: { latitudeDelta: 0.3, longitudeDelta: 0.3 },
    collection: 'restaurants - laguna + newport'
  },
  'miami': {
    name: 'Miami',
    description: 'South Beach & beyond culinary scene',
    center: { latitude: 25.7617, longitude: -80.1918 },
    span: { latitudeDelta: 0.3, longitudeDelta: 0.3 },
    collection: 'restaurants - miami'
  },
  'los-angeles': {
    name: 'Los Angeles',
    description: 'LA\'s diverse culinary landscape from West Hollywood to Venice',
    center: { latitude: 34.0522, longitude: -118.2437 },
    span: { latitudeDelta: 0.5, longitudeDelta: 0.5 },
    collection: 'restaurants - los angeles'
  }
};

const categories = {
  all: { name: 'All', icon: '📍' },
  restaurants: { name: 'Restaurants', icon: '🍽️' },
  'museums-galleries': { name: 'Museums & Galleries', icon: '🎨' },
  'hotels-rentals': { name: 'Hotels & Rentals', icon: '🏨' }
};

// MapKit JS token from environment variable
const MAPKIT_TOKEN = process.env.REACT_APP_MAPKIT_TOKEN;

const Guides = () => {
  const [selectedCity, setSelectedCity] = useState('santa-barbara');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [mapReady, setMapReady] = useState(false);
  const [allRestaurants, setAllRestaurants] = useState({});
  const [loading, setLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeContributorMap, setActiveContributorMap] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const mapInstance = useRef(null);
  const mapContainerRef = useRef(null);
  const annotationsRef = useRef([]);
  const mapkitInitialized = useRef(false);
  const componentMounted = useRef(true);

  // Video references for boomerang effect
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);

  // Window width detection with reactive state
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Resize listener to update window width
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Initial check
    handleResize();

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // ... (Keep video interaction and boomerang effect logic as is) ...
  // Mobile video interaction handler
  const [mobileVideoInteractionStarted, setMobileVideoInteractionStarted] = useState(false);

  useEffect(() => {
    if (windowWidth >= 1300) {
      // Desktop: Videos autoplay immediately
      setMobileVideoInteractionStarted(true);
      return;
    }

    if (mobileVideoInteractionStarted) return;

    const handleInteraction = () => {
      setMobileVideoInteractionStarted(true);

      // Start videos after interaction
      if (leftVideoRef.current) {
        leftVideoRef.current.play().catch(console.error);
      }
      if (rightVideoRef.current) {
        rightVideoRef.current.play().catch(console.error);
      }

      // Remove event listeners
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('click', handleInteraction);
    };

    window.addEventListener('touchstart', handleInteraction, { passive: true });
    window.addEventListener('scroll', handleInteraction, { passive: true });
    window.addEventListener('click', handleInteraction, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('click', handleInteraction);
    };
  }, [windowWidth, mobileVideoInteractionStarted]);

  // Boomerang video effect setup
  const setupBoomerangEffect = useCallback((videoElement) => {
    if (!videoElement) return;
    
    let isReversing = false;
    let cleanupFunctions = [];
    
    const handleTimeUpdate = () => {
      if (isReversing) {
        if (videoElement.currentTime <= 0.1) {
          // Reached beginning, start forward
          isReversing = false;
          videoElement.currentTime = 0;
          videoElement.play().catch(console.error);
        }
      }
    };
    
    const handleEnded = () => {
      if (!isReversing) {
        // Reached end, start reverse
        isReversing = true;
        videoElement.currentTime = videoElement.duration - 0.1;
        
        // Manual reverse playback
        const reverseInterval = setInterval(() => {
          if (videoElement.currentTime <= 0.1) {
            clearInterval(reverseInterval);
            isReversing = false;
            videoElement.currentTime = 0;
            videoElement.play().catch(console.error);
          } else {
            videoElement.currentTime -= 0.033; // ~30fps reverse
          }
        }, 33);
        
        cleanupFunctions.push(() => clearInterval(reverseInterval));
      }
    };
    
    const handleCanPlayThrough = () => {
      // Only auto-start on desktop or after mobile interaction
      if (windowWidth >= 1300 || mobileVideoInteractionStarted) {
        videoElement.play().catch(console.error);
      }
    };

    const handleLoadedData = () => {
      // Set slower playback speed
      videoElement.playbackRate = 0.6; // 60% speed
      // Optimize video quality settings
      videoElement.style.imageRendering = 'auto'; // Let browser choose best quality
      videoElement.style.filter = 'contrast(1.05) saturate(1.05) brightness(1.02)'; // Subtle enhancement

      // Only auto-start on desktop or after mobile interaction
      if (windowWidth >= 1300 || mobileVideoInteractionStarted) {
        videoElement.play().catch(console.error);
      }
    };
    
    // Add all event listeners
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
    videoElement.addEventListener('loadeddata', handleLoadedData);
    
    // Try to start playing immediately
    if (videoElement.readyState >= 3) { // HAVE_FUTURE_DATA
      handleLoadedData();
    }
    
    // Cleanup function
    const cleanup = () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      cleanupFunctions.forEach(fn => fn());
    };
    
    cleanupFunctions.push(cleanup);
    return cleanup;
  }, [windowWidth, mobileVideoInteractionStarted]);

  // Setup videos when refs are available
  useEffect(() => {
    // Cleanup any existing video effects when component unmounts
    return () => {
      if (leftVideoRef.current) {
        leftVideoRef.current.pause();
      }
      if (rightVideoRef.current) {
        rightVideoRef.current.pause();
      }
    };
  }, []);

  // Get appropriate token
  const getToken = useCallback(() => {
    return MAPKIT_TOKEN;
  }, []);

  // Analytics tracking for button clicks
  const trackButtonClick = useCallback((restaurantId, buttonType, restaurantName, city) => {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'guide_button_click', {
        restaurant_id: restaurantId,
        button_type: buttonType,
        restaurant_name: restaurantName,
        city: city,
        timestamp: Date.now()
      });
      console.log(`📊 Tracked ${buttonType} click for ${restaurantName}`);
    } catch (error) {
      console.warn('Analytics tracking error:', error);
    }
  }, []);

  // Generate Apple Maps URL
  const generateAppleMapsLink = useCallback((name, address) => {
    return `https://maps.apple.com/?q=${encodeURIComponent(`${name}, ${address}`)}`;
  }, []);

  // Helper functions - pass these to popup as props if needed, or keep logic inside popup
  const getFeatureIcon = useCallback((feature) => {
    // ... (Keep your existing icon map logic) ...
     const iconMap = {
      'Takeout': '🥡',
      'Delivery': '🚚',
      'Wheelchair Accessible': '♿',
      'Good for Kids': '👶',
      'Outdoor Dining': '🌳',
      'Parking': '🅿️',
      'Outdoor Parking': '🚗',
      'WiFi': '📶',
      'Reservations': '📋',
      'Credit Cards': '💳',
      'Reservations Required': '📅',
      'Wine Pairing': '🍷',
      'Michelin Star': '⭐'
    };
    return iconMap[feature] || '•';
  }, []);

  const getCurrentStatus = useCallback((restaurant) => {
     // ... (Keep your existing status logic) ...
    if (restaurant.liveData?.current_opening_hours?.open_now !== undefined) {
      return {
        isOpen: restaurant.liveData.current_opening_hours.open_now,
        source: 'live',
        confidence: 'high'
      };
    }
    
    if (restaurant.liveData?.opening_hours?.open_now !== undefined) {
      return {
        isOpen: restaurant.liveData.opening_hours.open_now,
        source: 'live',
        confidence: 'medium'
      };
    }

    const now = new Date();
    const currentHour = now.getHours();
    const restaurantType = restaurant.type?.toLowerCase() || '';

    if (currentHour >= 17 && currentHour < 22) {
      if (restaurantType.includes('breakfast') || restaurantType.includes('cafe')) {
        return { isOpen: false, source: 'estimated', confidence: 'high' };
      }
      return { isOpen: true, source: 'estimated', confidence: 'medium' };
    }

    if (currentHour >= 11 && currentHour < 15) {
      return { isOpen: true, source: 'estimated', confidence: 'medium' };
    }

    if (currentHour >= 6 && currentHour < 11) {
      if (restaurantType.includes('breakfast') || restaurantType.includes('cafe')) {
        return { isOpen: true, source: 'estimated', confidence: 'medium' };
      }
      return { isOpen: false, source: 'estimated', confidence: 'low' };
    }

    if (currentHour >= 22 || currentHour < 6) {
      if (restaurantType.includes('bar')) {
        return { isOpen: true, source: 'estimated', confidence: 'medium' };
      }
      return { isOpen: false, source: 'estimated', confidence: 'medium' };
    }

    return { isOpen: null, source: 'unknown', confidence: 'none' };
  }, []);

  const getCurrentHours = useCallback((restaurant) => {
      // ... (Keep your existing hours logic) ...
    if (restaurant.liveData?.current_opening_hours?.weekday_text) {
      const today = new Date().getDay();
      const dayIndex = today === 0 ? 6 : today - 1;
      return restaurant.liveData.current_opening_hours.weekday_text[dayIndex] || 'Hours not available';
    }

    if (restaurant.liveData?.opening_hours?.weekday_text) {
      const today = new Date().getDay();
      const dayIndex = today === 0 ? 6 : today - 1;
      return restaurant.liveData.opening_hours.weekday_text[dayIndex] || 'Hours not available';
    }

    if (restaurant.hours) {
      return restaurant.hours;
    }

    const restaurantType = restaurant.type?.toLowerCase() || '';
    if (restaurantType.includes('breakfast') || restaurantType.includes('cafe')) {
      return 'Typically open 7:00 AM - 3:00 PM';
    } else if (restaurantType.includes('bar')) {
      return 'Typically open 4:00 PM - 11:00 PM';
    } else if (restaurantType.includes('fine dining')) {
      return 'Typically open 5:30 PM - 9:30 PM';
    }
    return 'Typically open 11:00 AM - 9:00 PM';
  }, []);

  // Get all restaurant photos (returns array of URLs)
  const getRestaurantPhotos = useCallback((restaurant) => {
    const photos = [];

    if (restaurant.liveData?.photos && restaurant.liveData.photos.length > 0) {
      console.log('Restaurant photos data:', restaurant.name, restaurant.liveData.photos);

      // Get ALL photos from liveData, prioritize higher resolution
      // Limit to first 10 photos for performance
      restaurant.liveData.photos.slice(0, 10).forEach(photo => {
        let photoUrl = null;
        if (photo.url_1200) {
          photoUrl = photo.url_1200;
        } else if (photo.url_800) {
          photoUrl = photo.url_800;
        } else if (photo.url_400) {
          photoUrl = photo.url_400;
        } else if (photo.url) {
          photoUrl = photo.url;
        }

        // Proxy through Firebase function to avoid CORS issues
        if (photoUrl) {
          const proxiedUrl = `https://us-central1-a-r-t-i-f-a-c-t.cloudfunctions.net/proxyPhoto?url=${encodeURIComponent(photoUrl)}`;
          photos.push(proxiedUrl);
        }
      });

      console.log('Extracted photo URLs:', photos);
    } else {
      console.log('No photos found for:', restaurant.name, restaurant.liveData);
    }

    return photos;
  }, []);

  // Legacy single photo getter for compatibility
  const getRestaurantPhoto = useCallback((restaurant) => {
    const photos = getRestaurantPhotos(restaurant);
    return photos.length > 0 ? photos[0] : null;
  }, [getRestaurantPhotos]);

  // Get current city's restaurants
  const getCurrentRestaurants = useCallback(() => {
    const cityData = allRestaurants[selectedCity] || [];
    return cityData;
  }, [allRestaurants, selectedCity]);

  // Clean up map
  const cleanupMap = useCallback(() => {
    if (annotationsRef.current.length > 0 && mapInstance.current) {
      try {
        mapInstance.current.removeAnnotations(annotationsRef.current);
      } catch (error) {
        console.warn('Error removing annotations:', error);
     }
   }
   annotationsRef.current = [];
 }, []);

 // Create map instance only once
 const createMap = useCallback(() => {
   if (!mapContainerRef.current || !window.mapkit || !componentMounted.current || mapInstance.current) {
     return;
   }

   // Use the current selectedCity value at time of creation
   console.log('Selected city for popup:', selectedCity);
    const currentCity = cities[selectedCity];
    console.log('Current city data:', currentCity);

   try {
     const map = new window.mapkit.Map(mapContainerRef.current, {
       center: new window.mapkit.Coordinate(
         currentCity.center.latitude, 
         currentCity.center.longitude
       ),
       region: new window.mapkit.CoordinateRegion(
         new window.mapkit.Coordinate(
           currentCity.center.latitude, 
           currentCity.center.longitude
         ),
         new window.mapkit.CoordinateSpan(
           currentCity.span.latitudeDelta, 
           currentCity.span.longitudeDelta
         )
       ),
       mapType: window.mapkit.Map.MapTypes.Hybrid,
       showsMapTypeControl: true,
       showsZoomControl: true,
       showsUserLocationControl: true,
       showsPointsOfInterest: false,
       showsScale: window.mapkit.FeatureVisibility.Visible,
       tintColor: '#007AFF',
       colorScheme: window.mapkit.Map.ColorSchemes.Light
     });

     // CRITICAL FIX: Add proper select event listener
     map.addEventListener('select', (event) => {
       console.log('📍 Pin clicked! Event:', event);
       
       if (event.annotation && event.annotation.title) {
         console.log('🎯 Selected annotation:', event.annotation.title);
         
         // Find the restaurant that matches this annotation - search all cities
         let selectedRestaurant = null;
         
         // First try current city's restaurants
         const currentRestaurants = getCurrentRestaurants();
         selectedRestaurant = currentRestaurants.find(
           restaurant => restaurant.name === event.annotation.title
         );
         
         // If not found in current city, search all cities
         if (!selectedRestaurant) {
           for (const cityKey of Object.keys(allRestaurants)) {
             const cityRestaurants = allRestaurants[cityKey] || [];
             selectedRestaurant = cityRestaurants.find(
               restaurant => restaurant.name === event.annotation.title
             );
             if (selectedRestaurant) {
  console.log(`🔍 Found restaurant "${selectedRestaurant.name}" in ${cityKey}`);
  selectedRestaurant._foundInCity = cityKey; // Store which city we found it in
  break;
}
           }
         }
         
         if (selectedRestaurant) {
           console.log('✅ Found matching restaurant:', selectedRestaurant.name);
           setSelectedRestaurant(selectedRestaurant); // Set state instead of creating DOM element
         } else {
           console.warn('⚠️ No matching restaurant found for:', event.annotation.title);
         }
       } else {
         console.warn('⚠️ Select event missing annotation or title');
       }
     });

     mapInstance.current = map;
     setMapReady(true);
     console.log('✅ Map created successfully with select listener');

   } catch (mapCreationError) {
     console.error('Error creating map instance:', mapCreationError);
   }
 }, [selectedCity, getCurrentRestaurants]); // removed createCalloutElement

 // Update map region when city changes
 const updateMapRegion = useCallback(() => {
   if (!mapInstance.current || !mapReady) {
     return;
   }

   const currentCity = cities[selectedCity];

   try {
     const newRegion = new window.mapkit.CoordinateRegion(
       new window.mapkit.Coordinate(
         currentCity.center.latitude, 
         currentCity.center.longitude
       ),
       new window.mapkit.CoordinateSpan(
         currentCity.span.latitudeDelta, 
         currentCity.span.longitudeDelta
       )
     );

     mapInstance.current.setRegionAnimated(newRegion, true);
   } catch (error) {
     console.error('Error updating map region:', error);
   }
 }, [selectedCity, mapReady]);

 // Handle loading a contributor map
 const handleLoadContributorMap = useCallback((mapData) => {
   console.log('📍 Loading contributor map:', mapData.mapTitle);
   setActiveContributorMap(mapData);

   // Scroll to top to show the map
   window.scrollTo({ top: 0, behavior: 'smooth' });

   // TODO: Filter restaurants to only show ones in this map
   // For now, just showing the attribution banner
 }, []);

 // Handle closing contributor map view
 const handleCloseContributorMap = useCallback(() => {
   console.log('📍 Closing contributor map view');
   setActiveContributorMap(null);
 }, []);

 // Add annotations to map
 const addAnnotationsToMap = useCallback(() => {
   if (!mapInstance.current || !mapReady) {
     return;
   }

   const currentRestaurants = getCurrentRestaurants();

   const filteredRestaurants = currentRestaurants.filter(restaurant => {
     if (selectedCategory === 'all') return true;
     if (selectedCategory === 'restaurants') return restaurant.category === 'restaurants';
     if (selectedCategory === 'hotels-rentals') return restaurant.category === 'hotels-rentals';
     if (selectedCategory === 'museums-galleries') return restaurant.category === 'museums-galleries';
     return false;
   });

   if (filteredRestaurants.length === 0) {
     return;
   }

   try {
     const validRestaurants = filteredRestaurants.filter(restaurant => {
       if (!restaurant?.name) return false;
       
       const lat = restaurant.latitude;
       const lng = restaurant.longitude;
       
       if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
         console.warn(`Invalid coordinates for ${restaurant.name}`);
         return false;
       }
       
       return true;
     });

     if (validRestaurants.length === 0) {
       return;
     }

     // Clear existing annotations
     if (annotationsRef.current.length > 0) {
       try {
         mapInstance.current.removeAnnotations(annotationsRef.current);
       } catch (error) {
         console.warn('Error removing annotations:', error);
       }
       annotationsRef.current = [];
     }

     const newAnnotations = validRestaurants.map(restaurant => {
       const lat = Number(restaurant.latitude);
       const lng = Number(restaurant.longitude);

       const coordinate = new window.mapkit.Coordinate(lat, lng);

       // Get current status for color coding
       const status = getCurrentStatus(restaurant);

       // Determine marker color based on open/closed status
       // ONLY show green for confirmed live data, otherwise neutral/closed
       let markerClass = "custom-marker";
       if (status.isOpen === true && status.source === 'live') {
         markerClass += " marker-open"; // British Racing Green - only for confirmed open
       } else if (status.isOpen === false && (status.source === 'live' || status.confidence === 'high')) {
         markerClass += " marker-closed"; // Maroon - for confirmed closed or high confidence closed
       }
       // If status is unknown or just estimated, use default blue color (no additional class)

       // Add type-based styling
       const restaurantType = restaurant.type?.toLowerCase() || 'restaurant';
       if (restaurantType.includes('cafe') || restaurantType.includes('coffee')) {
         markerClass += " marker-cafe";
       } else if (restaurantType.includes('bar') || restaurantType.includes('pub')) {
         markerClass += " marker-bar";
       }

       // Create custom DOM element for "Jewel" marker (dot-to-pill effect)
       const element = document.createElement("div");
       element.className = markerClass;
       element.setAttribute("data-title", restaurant.name); // Displays in pill on hover

       // Store restaurant reference for click handling
       element.restaurantData = restaurant;

       // Add click handler directly to element
       element.addEventListener('click', (e) => {
         e.stopPropagation();
         setSelectedRestaurant(restaurant);
       });

       const annotation = new window.mapkit.Annotation(coordinate, () => element, {
         title: restaurant.name,
         subtitle: `${restaurant.type || 'Restaurant'} • ${restaurant.priceRange || '$'}`,
         displayPriority: 1000
       });

       // Store restaurant reference on annotation for event handling
       annotation.restaurantData = restaurant;

       return annotation;
     });

     if (newAnnotations.length > 0 && mapInstance.current) {
       mapInstance.current.addAnnotations(newAnnotations);
       annotationsRef.current = newAnnotations;
       console.log(`✅ Added ${newAnnotations.length} annotations with click detection`);
     }

   } catch (error) {
     console.error('Error adding annotations:', error);
   }
 }, [selectedCity, selectedCategory, getCurrentRestaurants, getCurrentStatus, mapReady]);

 // Load all restaurants data on mount using JSON fallback for iOS performance
useEffect(() => {
  componentMounted.current = true;
  
  const loadAllRestaurants = async () => {
    setLoading(true);
    
    try {
      // Skip Firebase - go straight to JSON fallback for faster iOS loading
      throw new Error('Using JSON fallback for faster iOS loading');
      
    } catch (err) {
      console.log('🔄 Loading guides data from guides.json...');
      
      try {
        const response = await fetch('/guides.json');
        const guidesData = await response.json();
        
        console.log(`📁 Loaded guides data for ${Object.keys(guidesData).length} cities`);
        console.log(`🍽️ Total restaurants: ${Object.values(guidesData).flat().length}`);
        
        setAllRestaurants(guidesData);
        setLoading(false);
        
      } catch (jsonError) {
        console.error('❌ Both Firebase and JSON failed:', jsonError);
        setAllRestaurants({});
        setLoading(false);
      }
    }
  };

  loadAllRestaurants();
  
  return () => {
    componentMounted.current = false;
  };
}, []);

 // Initialize MapKit only once
 useEffect(() => {
   if (mapkitInitialized.current || window.mapkit) {
     return;
   }

   mapkitInitialized.current = true;

   const script = document.createElement('script');
   script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
   script.crossOrigin = 'anonymous';

   script.onload = () => {
     try {
       window.mapkit.init({
         authorizationCallback: (done) => {
           done(getToken());
         }
       });

       console.log('✅ MapKit initialized');

       // Attempt map creation immediately after init
       setTimeout(() => {
         if (componentMounted.current && mapContainerRef.current && !mapInstance.current) {
           console.log('🗺️ Creating map after MapKit init...');
           createMap();
         }
       }, 500);

       window.mapkit.addEventListener('configuration-change', () => {
         console.log('🔄 MapKit configuration changed');

         // Backup: create map if it wasn't created yet
         setTimeout(() => {
           if (componentMounted.current && mapContainerRef.current && !mapInstance.current) {
             console.log('🗺️ Creating map after configuration change...');
             createMap();
           }
         }, 300);
       });

       window.mapkit.addEventListener('error', (error) => {
         console.error('MapKit error:', error);
       });

     } catch (error) {
       console.error('Map initialization failed:', error);
     }
   };

   script.onerror = () => {
     console.error('Failed to load MapKit JS');
   };

   document.head.appendChild(script);
 }, [getToken, createMap]);

 // Handle city changes and map creation
 useEffect(() => {
   if (!window.mapkit || !componentMounted.current) {
     console.log('⏸️ Waiting for MapKit or component mount...');
     return;
   }

   // Create map only once on initial load or if it doesn't exist
   if (!mapInstance.current) {
     console.log('🗺️ Creating map from city change effect...');
     createMap();
   } else {
     // Just update region if map already exists
     console.log('🔄 Updating map region for city change...');
     updateMapRegion();
   }
 }, [selectedCity, createMap, updateMapRegion]);

 // CRITICAL: Create map when container ref becomes available (primary fallback)
 useEffect(() => {
   if (window.mapkit && componentMounted.current && mapContainerRef.current && !mapInstance.current) {
     console.log('🗺️ Container ref available, creating map...');
     // Delay to ensure DOM is fully rendered
     const timeoutId = setTimeout(() => {
       if (componentMounted.current && !mapInstance.current) {
         createMap();
       }
     }, 200);

     return () => clearTimeout(timeoutId);
   }
 }, [createMap]);

 // CRITICAL: Force map creation after restaurants load (handles refresh case)
 useEffect(() => {
   if (window.mapkit && !mapInstance.current && !loading && componentMounted.current && mapContainerRef.current) {
     console.log('🗺️ Data loaded, ensuring map exists...');
     const timeoutId = setTimeout(() => {
       if (!mapInstance.current && componentMounted.current) {
         console.log('🔄 Creating map after data load...');
         createMap();
       }
     }, 400);

     return () => clearTimeout(timeoutId);
   }
 }, [loading, createMap]);

 // Add annotations when map is ready and restaurants are loaded
 useEffect(() => {
   if (mapReady && allRestaurants[selectedCity]?.length > 0 && componentMounted.current) {
     // Clear existing annotations first
     cleanupMap();
     
     const timeoutId = setTimeout(() => {
       if (mapReady && componentMounted.current) {
         addAnnotationsToMap();
       }
     }, 200);
     
     return () => {
       clearTimeout(timeoutId);
     };
   }
 }, [mapReady, allRestaurants, selectedCity, addAnnotationsToMap, cleanupMap]);

 // Cleanup on unmount
 useEffect(() => {
   return () => {
     componentMounted.current = false;
     
     // Only destroy map on actual component unmount
     if (mapInstance.current) {
       try {
         if (annotationsRef.current.length > 0) {
           mapInstance.current.removeAnnotations(annotationsRef.current);
         }
         mapInstance.current.destroy();
       } catch (error) {
         console.warn('Error destroying map on unmount:', error);
       }
       mapInstance.current = null;
     }
   };
 }, []);

 const currentCity = cities[selectedCity];
 const currentRestaurants = getCurrentRestaurants();
 const hasData = currentCity?.collection && currentRestaurants.length > 0;

 return (
  <>
    <div className="guides-container">
     {/* Left Sidebar Video Background - ALWAYS VISIBLE */}
     <div className="guides-left-video-strip">
       <video
         ref={leftVideoRef}
         src="/videos/jungle-ruins-arial.mov"
         style={{
           width: '100%',
           height: '100%',
           objectFit: 'cover',
           objectPosition: 'center'
         }}
         muted
         loop={false}
         playsInline
         preload="metadata"
         autoPlay={windowWidth >= 1300}
         onLoadedData={() => {
           // Setup boomerang effect when video loads
           if (leftVideoRef.current) {
             setupBoomerangEffect(leftVideoRef.current);
           }
         }}
         onError={(e) => {
           console.error('Left video error:', e);
         }}
       />
     </div>

     {/* Left Sidebar Content - only when windowWidth >= 1300 */}
     {windowWidth >= 1300 && (
       <div className="guides-left-sidebar">
         <div className="guides-sidebar-section" style={{
           background: 'rgba(255, 255, 255, 0.3)',
           borderRadius: '8px',
           padding: '0.2rem 1rem 1rem',
           marginLeft: '10px',
           marginRight: '10px',
           marginTop: '10px',
           marginBottom: '20px',
           minHeight: '650px'
         }}>
           <h3>City</h3>
           <div style={{ 
             display: 'flex', 
             flexDirection: 'column', 
             gap: '0.5rem',
             maxHeight: '300px',
             overflowY: 'auto',
             marginTop: '0rem',
             marginBottom: '2rem'
           }}>
             {Object.entries(cities).map(([cityKey, city]) => (
               <button
                 key={cityKey}
                 onClick={() => {
                   console.log('🏙️ Switching city from', selectedCity, 'to', cityKey);
                   setSelectedCity(cityKey);
                 }}
                 className={selectedCity === cityKey ? 'selected' : ''}
               >
                 {city.name}
               </button>
             ))}
           </div>

           <h3>Type</h3>
           <div style={{ 
             display: 'flex', 
             flexDirection: 'column', 
             gap: '0.5rem',
             maxHeight: '200px',
             overflowY: 'auto',
             marginTop: '0rem',
             marginBottom: '1rem'
           }}>
             {Object.entries(categories).map(([categoryKey, category]) => (
               <button
                 key={categoryKey}
                 onClick={() => setSelectedCategory(categoryKey)}
                 className={selectedCategory === categoryKey ? 'selected' : ''}
               >
                 {category.icon} {category.name}
               </button>
             ))}
           </div>

           <div style={{
             fontSize: '11px',
             color: '#666',
             fontStyle: 'italic',
             textAlign: 'center',
             padding: '0.5rem',
             marginTop: '0.5rem'
           }}>
             Scroll for more
           </div>
         </div>
       </div>
     )}

     {/* Map Container */}
     <div className={`guides-main ${windowWidth < 1300 ? 'mobile-layout' : ''}`}>
       {/* Contributor Attribution Banner */}
       {activeContributorMap && (
         <ContributorAttributionBanner
           mapData={activeContributorMap}
           onClose={handleCloseContributorMap}
         />
       )}

       <div className="guides-map-container">
         {loading ? (
           <div className="guides-loading-state">
             <div>Loading restaurants...</div>
           </div>
         ) : !hasData ? (
           <div className="guides-empty-state">
             <div className="empty-icon">🗺️</div>
             <h3>Coming Soon</h3>
             <p>
               We're working on mapping {categories[selectedCategory].name.toLowerCase()} for {currentCity.name}.
             </p>
             {!currentCity?.collection && (
               <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#999' }}>
                 This city will be available in a future update.
               </p>
             )}
           </div>
         ) : currentRestaurants.length === 0 ? (
           <div className="guides-empty-state">
             <div className="empty-icon">🔍</div>
             <h3>No Restaurants Found</h3>
             <p>
               No restaurants found for {currentCity.name}. Try another location or check your data source.
             </p>
           </div>
         ) : (
           <>
             <div ref={mapContainerRef} className="guides-map-inner"></div>
             {!mapReady && (
               <div className="guides-loading-state" style={{
                 position: 'absolute',
                 top: 0,
                 left: 0,
                 width: '100%',
                 height: '100%',
                 background: 'rgba(255, 255, 255, 0.95)',
                 zIndex: 100
               }}>
                 <div>Initializing map...</div>
               </div>
             )}
           </>
         )}

         {/* Mobile Navigation Button - shows when windowWidth < 1300 */}
         {windowWidth < 1300 && (
           <div style={{
             position: 'absolute',
             bottom: '120px',
             left: '50%',
             transform: 'translateX(-50%)',
             zIndex: 100,
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center'
           }}>
             <div style={{
               position: 'absolute',
               top: '-13.6px',
               left: '-13.6px',
               right: '-13.6px',
               bottom: '-13.6px',
               background: 'rgba(255, 255, 255, 0.3)',
               backdropFilter: 'blur(10px)',
               borderRadius: '8px',
               zIndex: -1,
               boxShadow: `0 0 8px rgba(255, 255, 255, 1),
                          0 0 16px rgba(255, 255, 255, 1),
                          0 0 24px rgba(255, 255, 255, 1),
                          0 0 32px rgba(255, 255, 255, 1),
                          0 0 48px rgba(255, 255, 255, 1),
                          0 0 64px rgba(255, 255, 255, 1)`
             }} />
             <button
               onClick={() => setMobileNavOpen(true)}
               style={{
                 position: 'relative',
                 background: 'rgba(255, 255, 255, 0.3)',
                 backdropFilter: 'blur(10px)',
                 border: 'none',
                 borderRadius: '8px',
                 padding: '0.8rem 1.5rem',
                 fontSize: '9.5px',
                 fontWeight: 'bold',
                 textTransform: 'uppercase',
                 letterSpacing: '0.17rem',
                 color: '#001f4d',
                 cursor: 'pointer',
                 textAlign: 'center',
                 textShadow: `-0.25px -0.25px 0 rgba(255, 255, 255, 1), 0.25px -0.25px 0 rgba(255, 255, 255, 1), -0.25px 0.25px 0 rgba(255, 255, 255, 1), 0.25px 0.25px 0 rgba(255, 255, 255, 1), 0 0 8px rgba(255, 255, 255, 1), 0 0 16px rgba(255, 255, 255, 1), 0 0 24px rgba(255, 255, 255, 1), 0 0 32px rgba(255, 255, 255, 1), 0 0 48px rgba(255, 255, 255, 1), 0 0 64px rgba(255, 255, 255, 1)`,
                 transition: 'all 0.3s ease'
               }}
             >
               Navigate Guides
             </button>
           </div>
         )}
       </div>

       {/* Swipe Up Indicator */}
       <div className="swipe-indicator">
         <div className="swipe-indicator-line" />
         <div className="swipe-indicator-text">Swipe Up</div>
       </div>
     </div>

     {/* Right Sidebar Video Background - ALWAYS VISIBLE */}
     <div className="guides-right-video-strip">
       <video
         ref={rightVideoRef}
         src="/videos/jungle-ruins-arial.mov"
         muted
         loop={false}
         playsInline
         preload="auto"
         autoPlay={windowWidth >= 1300}
         onLoadedData={() => {
           if (rightVideoRef.current) {
             setupBoomerangEffect(rightVideoRef.current);
           }
         }}
         onError={(e) => {
           console.error('Right video error:', e);
         }}
       />
     </div>

     {/* Right Sidebar Content - only when windowWidth >= 1300 */}
     {windowWidth >= 1300 && (
       <div className="guides-right-sidebar">
         
         {/* Recommended section */}
         <div className="sidebar-section">
           <div className="section-header">
             <h3>Recommended</h3>
           </div>
           
           <div className="sidebar-scroll-section">
             {currentRestaurants.slice(0, 15).map((restaurant, index) => (
               <button 
                 key={index}
                 onClick={() => {
                   setSelectedRestaurant(restaurant);
                 }}
               >
                 {restaurant.name.length > 25 ? restaurant.name.substring( 0, 25) + '...' : restaurant.name}
               </button>
             ))}
           </div>

           <div className="sidebar-hint">
             Scroll for more
           </div>
         </div>
         
         {/* Footer */}
         <div className="sidebar-footer">
           <div className="footer-links">
             <span>About</span>
             <span>Help</span>
             <span>Terms</span>
             <span>Privacy</span>
           </div>
           <div className="copyright">
             © Artifex {new Date().getFullYear()}
           </div>
         </div>
       </div>
     )}

     {/* Mobile Navigation Popup - shows when windowWidth < 1300 */}
     {windowWidth < 1300 && (
       <>
         {/* Mobile Navigation Popup */}
         {mobileNavOpen && (
           <div
             onClick={() => setMobileNavOpen(false)}
             style={{
               position: 'fixed',
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               background: 'rgba(0, 0, 0, 0.6)',
               zIndex: 1000,
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               padding: '20px'
             }}
           >
             <div
               onClick={(e) => e.stopPropagation()}
               style={{
                 background: 'white',
                 borderRadius: '12px',
                 width: '100%',
                 maxWidth: '520px',
                 maxHeight: '90vh',
                 overflowY: 'auto',
                 padding: '32px 28px 28px',
                 boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                 position: 'relative'
               }}
             >
               <button
                 onClick={() => setMobileNavOpen(false)}
                 style={{
                   position: 'absolute',
                   top: '12px',
                   right: '12px',
                   background: 'rgba(0, 31, 77, 0.05)',
                   border: '1px solid rgba(0, 31, 77, 0.1)',
                   borderRadius: '50%',
                   fontSize: '20px',
                   color: '#001f4d',
                   cursor: 'pointer',
                   width: '32px',
                   height: '32px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   transition: 'all 0.2s ease'
                 }}
                 onMouseOver={(e) => {
                   e.currentTarget.style.background = 'rgba(0, 31, 77, 0.1)';
                 }}
                 onMouseOut={(e) => {
                   e.currentTarget.style.background = 'rgba(0, 31, 77, 0.05)';
                 }}
               >
                 ×
               </button>

               <h2 style={{
                 margin: '0 0 24px 0',
                 fontSize: '1.75rem',
                 fontWeight: 300,
                 color: '#001f4d',
                 fontFamily: 'Georgia, serif',
                 letterSpacing: '-0.02em'
               }}>Navigate Guides</h2>

               {/* City Selection */}
               <div style={{ marginBottom: '24px' }}>
                 <h3 style={{
                   fontSize: '0.7rem',
                   fontWeight: 500,
                   textTransform: 'uppercase',
                   letterSpacing: '0.1rem',
                   marginBottom: '10px',
                   color: '#666',
                   fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
                 }}>Cities</h3>
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                   gap: '8px'
                 }}>
                   {Object.entries(cities).map(([cityKey, city]) => (
                     <button
                       key={cityKey}
                       onClick={() => {
                         setSelectedCity(cityKey);
                       }}
                       style={{
                         padding: '10px 14px',
                         background: selectedCity === cityKey ? '#001f4d' : 'white',
                         color: selectedCity === cityKey ? 'white' : '#001f4d',
                         border: `1px solid ${selectedCity === cityKey ? '#001f4d' : '#ddd'}`,
                         borderRadius: '6px',
                         fontSize: '0.875rem',
                         fontWeight: selectedCity === cityKey ? 500 : 400,
                         cursor: 'pointer',
                         textAlign: 'center',
                         transition: 'all 0.2s ease',
                         fontFamily: 'Georgia, serif',
                         boxShadow: selectedCity === cityKey ? '0 2px 8px rgba(0, 31, 77, 0.2)' : 'none'
                       }}
                       onMouseOver={(e) => {
                         if (selectedCity !== cityKey) {
                           e.currentTarget.style.background = '#f5f5f5';
                           e.currentTarget.style.borderColor = '#001f4d';
                         }
                       }}
                       onMouseOut={(e) => {
                         if (selectedCity !== cityKey) {
                           e.currentTarget.style.background = 'white';
                           e.currentTarget.style.borderColor = '#ddd';
                         }
                       }}
                     >
                       {city.name}
                     </button>
                   ))}
                 </div>
               </div>

               {/* Category Selection */}
               <div style={{ marginBottom: '20px' }}>
                 <h3 style={{
                   fontSize: '0.7rem',
                   fontWeight: 500,
                   textTransform: 'uppercase',
                   letterSpacing: '0.1rem',
                   marginBottom: '10px',
                   color: '#666',
                   fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
                 }}>Types</h3>
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                   gap: '8px'
                 }}>
                   {Object.entries(categories).map(([categoryKey, category]) => (
                     <button
                       key={categoryKey}
                       onClick={() => {
                         setSelectedCategory(categoryKey);
                       }}
                       style={{
                         padding: '10px 12px',
                         background: selectedCategory === categoryKey ? '#001f4d' : 'white',
                         color: selectedCategory === categoryKey ? 'white' : '#001f4d',
                         border: `1px solid ${selectedCategory === categoryKey ? '#001f4d' : '#ddd'}`,
                         borderRadius: '6px',
                         fontSize: '0.8rem',
                         fontWeight: selectedCategory === categoryKey ? 500 : 400,
                         cursor: 'pointer',
                         textAlign: 'center',
                         display: 'flex',
                         flexDirection: 'column',
                         alignItems: 'center',
                         justifyContent: 'center',
                         gap: '4px',
                         transition: 'all 0.2s ease',
                         fontFamily: 'Georgia, serif',
                         minHeight: '70px',
                         boxShadow: selectedCategory === categoryKey ? '0 2px 8px rgba(0, 31, 77, 0.2)' : 'none'
                       }}
                       onMouseOver={(e) => {
                         if (selectedCategory !== categoryKey) {
                           e.currentTarget.style.background = '#f5f5f5';
                           e.currentTarget.style.borderColor = '#001f4d';
                         }
                       }}
                       onMouseOut={(e) => {
                         if (selectedCategory !== categoryKey) {
                           e.currentTarget.style.background = 'white';
                           e.currentTarget.style.borderColor = '#ddd';
                         }
                       }}
                     >
                       <span style={{ fontSize: '20px' }}>{category.icon}</span>
                       <span style={{ fontSize: '0.75rem', lineHeight: 1.2 }}>{category.name}</span>
                     </button>
                   ))}
                 </div>
               </div>

               <button
                 onClick={() => setMobileNavOpen(false)}
                 style={{
                   width: '100%',
                   padding: '16px',
                   background: '#001f4d',
                   color: 'white',
                   border: '1px solid #001f4d',
                   borderRadius: '12px',
                   fontSize: '11px',
                   fontWeight: 'bold',
                   textTransform: 'uppercase',
                   letterSpacing: '0.15rem',
                   cursor: 'pointer',
                   marginTop: '12px',
                   boxShadow: '0 6px 20px rgba(0, 31, 77, 0.3)',
                   transition: 'all 0.2s ease'
                 }}
                 onMouseOver={(e) => {
                   e.currentTarget.style.transform = 'translateY(-2px)';
                   e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 31, 77, 0.4)';
                 }}
                 onMouseOut={(e) => {
                   e.currentTarget.style.transform = 'translateY(0)';
                   e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 31, 77, 0.3)';
                 }}
               >
                 Apply & Close
               </button>
             </div>
           </div>
         )}
       </>
     )}
    </div>

    {/* Contributor Maps Section - Below the map */}
    <ContributorMapsSection
      currentCity={selectedCity}
      onLoadMap={handleLoadContributorMap}
      allRestaurants={allRestaurants}
    />

    {/* Restaurant Popup - React Component */}
    {selectedRestaurant && (
      <RestaurantPopup
        restaurant={selectedRestaurant}
        currentCity={cities[selectedRestaurant._foundInCity || selectedCity]}
        onClose={() => setSelectedRestaurant(null)}
        onTrackButtonClick={trackButtonClick}
        getCurrentStatus={getCurrentStatus}
        getCurrentHours={getCurrentHours}
        getRestaurantPhoto={getRestaurantPhoto}
        getRestaurantPhotos={getRestaurantPhotos}
        generateAppleMapsLink={generateAppleMapsLink}
      />
    )}
  </>
 );
};

export default Guides;